import { FileIcon, FolderIcon } from 'lucide-react';
import { type JSX, type ReactNode, useState } from 'react';

import { Button } from '@/components/ui/button.js';
import { PickerSearchBox } from '@/features/microsoft/PickerSearchBox.js';
import {
  searchOneDriveSetupItems,
  type OneDriveSetupHit,
} from '@/lib/api/onedrive.js';

import {
  oneDriveErrorPresentation,
  type OneDriveErrorPresentation,
} from './onedrive-error.js';

/** What a finished search found. */
interface FoundItems {
  query: string;
  hits: OneDriveSetupHit[];
  nextCursor: string | null;
}

/** Where a hit lives, in the names a person would click through. */
function hitPath(hit: OneDriveSetupHit): string {
  return ['OneDrive', ...hit.ancestors.map((folder) => folder.name)].join(
    ' / ',
  );
}

/**
 * The search box above the OneDrive tree: it finds folders and files by name and content across
 * the whole drive, and Show opens the folder that holds a hit, with the hit first — the same way
 * the SharePoint half does, so one dialog has one way to find things.
 *
 * A hit is not added from the results. The tree is where a row knows whether a folder on the list
 * already covers it, and it knows that from the path Show opens.
 */
export function OneDriveFinder({
  workspaceId,
  setupToken,
  disabled,
  onReveal,
  children,
}: {
  workspaceId: string;
  setupToken: string;
  /** Set while the tree is busy, so a search cannot race a level that is loading. */
  disabled: boolean;
  /**
   * Opens the folder that holds a hit, with the hit first. Answers whether it did, so a failed
   * open keeps the results on screen.
   */
  onReveal: (hit: OneDriveSetupHit) => Promise<boolean>;
  /**
   * The tree, shown whenever no search is on screen. It is inert while a search, a link or a Show
   * is in flight, so a browse started meanwhile cannot race the answer into the same tree.
   */
  children: ReactNode;
}): JSX.Element {
  const [text, setText] = useState('');
  const [found, setFound] = useState<FoundItems | null>(null);
  const [pending, setPending] = useState<'find' | 'more' | null>(null);
  /** The cause and the way out, both shown — the same pair every other OneDrive surface renders. */
  const [error, setError] = useState<OneDriveErrorPresentation | null>(null);

  function clear(): void {
    setText('');
    setFound(null);
    setError(null);
  }

  async function submit(): Promise<void> {
    const query = text.trim();
    if (!query || pending) return;
    setPending('find');
    setError(null);
    try {
      const page = await searchOneDriveSetupItems(
        workspaceId,
        setupToken,
        query,
      );
      setFound({ query, hits: page.items, nextCursor: page.nextCursor });
    } catch (reason) {
      setFound(null);
      setError(oneDriveErrorPresentation(reason));
    } finally {
      setPending(null);
    }
  }

  /**
   * The next page of hits, added only to the search it was asked for: a Clear or a new search while
   * it was in flight must not bring the old results back.
   */
  async function loadMore(): Promise<void> {
    if (!found?.nextCursor || pending) return;
    const query = found.query;
    setPending('more');
    setError(null);
    try {
      const page = await searchOneDriveSetupItems(
        workspaceId,
        setupToken,
        found.query,
        { cursor: found.nextCursor },
      );
      setFound((current) =>
        current?.query === query
          ? {
              ...current,
              hits: [...current.hits, ...page.items],
              nextCursor: page.nextCursor,
            }
          : current,
      );
    } catch (reason) {
      setError(oneDriveErrorPresentation(reason));
    } finally {
      setPending(null);
    }
  }

  /** Opens a hit in the tree; the finder waits for it like any request, so nothing races it. */
  async function reveal(hit: OneDriveSetupHit): Promise<void> {
    if (pending) return;
    setPending('find');
    try {
      if (await onReveal(hit)) clear();
    } finally {
      setPending(null);
    }
  }

  return (
    <>
      <PickerSearchBox
        value={text}
        onChange={setText}
        onSubmit={() => void submit()}
        onClear={clear}
        placeholder="Search OneDrive"
        label="Search OneDrive"
        searching={pending !== null}
        showClear={Boolean(text || found)}
        disabled={disabled}
      />
      {error && (
        <div className="shrink-0 border-b border-border px-2 py-2 text-sm">
          <p className="text-destructive">{error.message}</p>
          <p className="text-muted-foreground">{error.remediation}</p>
        </div>
      )}
      {found === null ? (
        <div className="contents" inert={pending !== null}>
          {children}
        </div>
      ) : (
        <div className="flex min-h-0 flex-1 flex-col gap-1 overflow-y-auto p-2">
          {found.hits.length === 0 && (
            <p className="px-2 py-3 text-sm text-muted-foreground">
              Nothing in OneDrive matched this search.
            </p>
          )}
          {found.hits.map((hit) => (
            <div
              key={hit.itemId}
              className="flex items-start gap-2 rounded-md px-2 py-2 text-sm transition-colors hover:bg-muted"
            >
              {hit.kind === 'folder' ? (
                <FolderIcon
                  className="mt-0.5 size-4 shrink-0 text-muted-foreground"
                  aria-hidden
                />
              ) : (
                <FileIcon
                  className="mt-0.5 size-4 shrink-0 text-muted-foreground"
                  aria-hidden
                />
              )}
              <span className="flex min-w-0 flex-1 flex-col">
                <span className="truncate">{hit.name}</span>
                <span className="truncate text-xs text-muted-foreground">
                  {hitPath(hit)}
                </span>
              </span>
              <Button
                size="sm"
                variant="outline"
                aria-label={`Show ${hit.name}`}
                disabled={pending !== null || disabled}
                onClick={() => void reveal(hit)}
              >
                Show
              </Button>
            </div>
          ))}
          {found.nextCursor && (
            <Button
              variant="outline"
              className="self-start"
              disabled={pending !== null}
              onClick={() => void loadMore()}
            >
              {pending === 'more' ? 'Loading more…' : 'Load more'}
            </Button>
          )}
        </div>
      )}
    </>
  );
}
